import CurrencyInputCell from '@/components/logbook-from-router/_components/currency-input-cell';
import ClearFindInViewFilter from '@/components/ui/find-in-view-filter/clear-find-in-view-filter';
import FindInViewFilter from '@/components/ui/find-in-view-filter/find-in-view-filter';
import { useFindInViewFilterContext } from '@/components/ui/find-in-view-filter/find-in-view-filter-provider';
import { useGetFocusId } from '@/components/ux/hooks/use-scroll-and-focus';
import { useLogbookContext } from '@/contexts/logbook-provider';
import useGoToDeal from '@/hooks/use-go-to-deal';
import type {
  AugmentedLogbookRecord,
  LogbookRecord,
} from '@/hooks/use-logbook';
import { centsToDollars } from '@/lib/currency';
import { cn } from '@/lib/utils';
import type { Deal } from '@/models/types';
import { createFileRoute } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import { CheckCircle, RefreshCw } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';

export const Route = createFileRoute('/(authenticated)/$hub/logbook/')({
  component: LogbookPage,
});

const CELL_CLASSNAME =
  'flex flex-col gap-2 border-black first:border-l border-r border-b overflow-hidden h-5 p-[1px]';
const ACTIVE_CELL_CLASSNAME = 'p-0 border-2 border-t border-l border-black';

type ColumnId =
  | 'VIN'
  | 'CDK_ID'
  | 'FULL_NAME'
  | 'CDK_STATUS'
  | 'CONTRACT_DATE'
  | 'CASH_PRICE'
  | 'CASH_DOWN'
  | 'PURCHASE_PRICE_SUBTOTAL'
  | 'DELIVERY_FEE'
  | 'NET_TRADE'
  | 'LIENHOLDER'
  | 'REGISTRATION_STATE'
  | 'RETURN_WINDOW'
  | 'CA_TAX_FEES_REQUIRED'
  | 'TAXES_PAID'
  | 'DMV_FEES_PAID'
  | 'LESS_PENALTIES'
  | 'TOTAL_DMV_PAYMENT'
  | 'EVER_FEE_REFUND'
  | 'AMOUNT_CHARGED'
  | 'ACTUAL_AMOUNT'
  | 'REFUND'
  | 'IRS_DOWN_PAYMENT'
  | 'IRS_SALES_PRICE';

const COLUMNS = [
  'VIN',
  'CDK_ID',
  'FULL_NAME',
  'CDK_STATUS',
  'CONTRACT_DATE',
  'PURCHASE_PRICE_SUBTOTAL',
  'CASH_PRICE',
  'DELIVERY_FEE',
  'NET_TRADE',
  'REGISTRATION_STATE',
  'IRS_DOWN_PAYMENT',
  'IRS_SALES_PRICE',
  'LIENHOLDER',
  'CASH_DOWN',
  'RETURN_WINDOW',
  'CA_TAX_FEES_REQUIRED',
  'AMOUNT_CHARGED',
  'TAXES_PAID',
  'DMV_FEES_PAID',
  'LESS_PENALTIES',
  'TOTAL_DMV_PAYMENT',
  'ACTUAL_AMOUNT',
  'REFUND',
  'EVER_FEE_REFUND',
] as const;

const FROZEN_COLUMNS: ColumnId[] = ['VIN', 'CDK_ID', 'FULL_NAME'] as const;

const COLUMN_WIDTHS: Record<ColumnId, string> = {
  VIN: 'w-44',
  CDK_ID: 'w-16',
  FULL_NAME: 'w-60',
  CDK_STATUS: 'w-28',
  CONTRACT_DATE: 'w-28',
  PURCHASE_PRICE_SUBTOTAL: 'w-40',
  CASH_PRICE: 'w-28',
  DELIVERY_FEE: 'w-28',
  NET_TRADE: 'w-20',
  REGISTRATION_STATE: 'w-30',
  IRS_DOWN_PAYMENT: 'w-32',
  IRS_SALES_PRICE: 'w-28',
  LIENHOLDER: 'w-20',
  CASH_DOWN: 'w-28',
  RETURN_WINDOW: 'w-28',
  CA_TAX_FEES_REQUIRED: 'w-28',
  AMOUNT_CHARGED: 'w-28',
  TAXES_PAID: 'w-20',
  DMV_FEES_PAID: 'w-28',
  LESS_PENALTIES: 'w-28',
  TOTAL_DMV_PAYMENT: 'w-32',
  ACTUAL_AMOUNT: 'w-28',
  REFUND: 'w-28',
  EVER_FEE_REFUND: 'w-28',
};

// DEV NOTE: SCROLL-TWO-VIRTUALIZED-LISTS-TOGETHER-HACK
// When the two lists (frozen and unfrozen) are virtualized
// the scroll is not in sync between the two lists
// Thus we need to disable the scroll on the two lists
// let the scroll event bubble up to the document
// and then update the scroll position of the two lists manually
// in order to keep them in sync

const FrozenRowFragment = ({
  logbookRecord,
  setActiveColumn,
  isActiveRow,
  activeColumn,
}: {
  logbookRecord: AugmentedLogbookRecord;
  setActiveColumn: (column: ColumnId) => void;
  isActiveRow: boolean;
  activeColumn: ColumnId | null;
}) => {
  const { goToDeal } = useGoToDeal();
  const hasMatchingDeal = !!logbookRecord?.deal;

  return (
    <div className="flex flex-row gap-0 items-center whitespace-nowrap">
      <div
        className={cn(CELL_CLASSNAME, COLUMN_WIDTHS.VIN, {
          [ACTIVE_CELL_CLASSNAME]: activeColumn === 'VIN' && isActiveRow,
        })}
        onClick={() => {
          setActiveColumn('VIN');
        }}
      >
        {logbookRecord.vin}
      </div>
      <div
        className={cn(CELL_CLASSNAME, COLUMN_WIDTHS.CDK_ID, {
          'cursor-pointer hover:underline text-blue-500': hasMatchingDeal,
          [ACTIVE_CELL_CLASSNAME]: activeColumn === 'CDK_ID' && isActiveRow,
        })}
        onClick={() => {
          setActiveColumn('CDK_ID');
        }}
      >
        <a
          onClick={() => {
            if (!logbookRecord?.deal) return;
            goToDeal({
              dealShortId: logbookRecord?.deal?.short_id,
            });
          }}
        >
          {logbookRecord.cdk_deal_id}
        </a>
      </div>
      <div
        className={cn(CELL_CLASSNAME, COLUMN_WIDTHS.FULL_NAME, {
          [ACTIVE_CELL_CLASSNAME]: activeColumn === 'FULL_NAME' && isActiveRow,
        })}
        onClick={() => {
          setActiveColumn('FULL_NAME');
        }}
      >
        {logbookRecord.name}
      </div>
    </div>
  );
};

const UnfrozenRowFragment = ({
  logbookRecord,
  setActiveColumn,
  isActiveRow,
  activeColumn,
}: {
  logbookRecord: AugmentedLogbookRecord;
  setActiveColumn: (column: ColumnId) => void;
  isActiveRow: boolean;
  activeColumn: ColumnId | null;
}) => {
  const { setIsSaving, setLastSaved } = useLogBookContext();

  const onSubmit = () => {
    setIsSaving(true);
  };

  const onSuccess = () => {
    setIsSaving(false);
    setLastSaved(dayjs().toDate());
  };

  const onError = () => {
    setIsSaving(false);
  };

  return (
    <div className="flex flex-row gap-0 items-center whitespace-nowrap">
      <div
        className={cn(CELL_CLASSNAME, COLUMN_WIDTHS.CDK_STATUS, {
          [ACTIVE_CELL_CLASSNAME]: activeColumn === 'CDK_STATUS' && isActiveRow,
        })}
        onClick={() => {
          setActiveColumn('CDK_STATUS');
        }}
      >
        {logbookRecord.cdk_deal_status}
      </div>
      <div
        className={cn(
          CELL_CLASSNAME,
          'text-right',
          COLUMN_WIDTHS.CONTRACT_DATE,
          {
            [ACTIVE_CELL_CLASSNAME]:
              activeColumn === 'CONTRACT_DATE' && isActiveRow,
          },
        )}
        onClick={() => {
          setActiveColumn('CONTRACT_DATE');
        }}
      >
        {logbookRecord.contract_date
          ? dayjs(logbookRecord.contract_date).format('MM/DD/YYYY')
          : ''}
      </div>

      {/* funding & registration */}
      <div
        className={cn(
          CELL_CLASSNAME,
          'text-right',
          COLUMN_WIDTHS.PURCHASE_PRICE_SUBTOTAL,
          {
            [ACTIVE_CELL_CLASSNAME]:
              activeColumn === 'PURCHASE_PRICE_SUBTOTAL' && isActiveRow,
          },
        )}
        onClick={() => {
          setActiveColumn('PURCHASE_PRICE_SUBTOTAL');
        }}
      >
        ${centsToDollars(logbookRecord.purchase_price_subtotal)}
      </div>
      <div
        className={cn(CELL_CLASSNAME, 'text-right', COLUMN_WIDTHS.CASH_PRICE, {
          [ACTIVE_CELL_CLASSNAME]: activeColumn === 'CASH_PRICE' && isActiveRow,
        })}
        onClick={() => {
          setActiveColumn('CASH_PRICE');
        }}
      >
        ${centsToDollars(logbookRecord.cash_price)}
      </div>
      <div
        className={cn(
          CELL_CLASSNAME,
          'text-right',
          COLUMN_WIDTHS.DELIVERY_FEE,
          {
            [ACTIVE_CELL_CLASSNAME]:
              activeColumn === 'DELIVERY_FEE' && isActiveRow,
          },
        )}
        onClick={() => {
          setActiveColumn('DELIVERY_FEE');
        }}
      >
        ${centsToDollars(logbookRecord.delivery_fee)}
      </div>
      <div
        className={cn(CELL_CLASSNAME, 'text-right', COLUMN_WIDTHS.NET_TRADE, {
          [ACTIVE_CELL_CLASSNAME]: activeColumn === 'NET_TRADE' && isActiveRow,
        })}
        onClick={() => {
          setActiveColumn('NET_TRADE');
        }}
      >
        ${centsToDollars(logbookRecord.net_trade)}
      </div>

      <div
        className={cn(
          CELL_CLASSNAME,
          'text-right',
          COLUMN_WIDTHS.REGISTRATION_STATE,
          {
            [ACTIVE_CELL_CLASSNAME]:
              activeColumn === 'REGISTRATION_STATE' && isActiveRow,
          },
        )}
        onClick={() => {
          setActiveColumn('REGISTRATION_STATE');
        }}
      >
        {logbookRecord.registration_state}
      </div>

      {/* // IRS */}
      <div
        className={cn(
          CELL_CLASSNAME,
          'text-right',
          COLUMN_WIDTHS.IRS_DOWN_PAYMENT,
          {
            [ACTIVE_CELL_CLASSNAME]:
              activeColumn === 'IRS_DOWN_PAYMENT' && isActiveRow,
          },
        )}
        onClick={() => {
          setActiveColumn('IRS_DOWN_PAYMENT');
        }}
      >
        ${centsToDollars(logbookRecord.irs_down_payment)}
      </div>
      <div
        className={cn(
          CELL_CLASSNAME,
          'text-right',
          COLUMN_WIDTHS.IRS_SALES_PRICE,
          {
            [ACTIVE_CELL_CLASSNAME]:
              activeColumn === 'IRS_SALES_PRICE' && isActiveRow,
          },
        )}
        onClick={() => {
          setActiveColumn('IRS_SALES_PRICE');
        }}
      >
        ${centsToDollars(logbookRecord.irs_sales_price)}
      </div>

      <div
        className={cn(CELL_CLASSNAME, 'text-right', COLUMN_WIDTHS.LIENHOLDER, {
          [ACTIVE_CELL_CLASSNAME]: activeColumn === 'LIENHOLDER' && isActiveRow,
        })}
        onClick={() => {
          setActiveColumn('LIENHOLDER');
        }}
      >
        {logbookRecord.lienholder}
      </div>
      <div
        className={cn(CELL_CLASSNAME, 'text-right', COLUMN_WIDTHS.CASH_DOWN, {
          [ACTIVE_CELL_CLASSNAME]: activeColumn === 'CASH_DOWN' && isActiveRow,
        })}
        onClick={() => {
          setActiveColumn('CASH_DOWN');
        }}
      >
        ${centsToDollars(logbookRecord.cash_down)}
      </div>
      <div
        className={cn(
          CELL_CLASSNAME,
          'text-right',
          COLUMN_WIDTHS.RETURN_WINDOW,
          {
            [ACTIVE_CELL_CLASSNAME]:
              activeColumn === 'RETURN_WINDOW' && isActiveRow,
          },
        )}
        onClick={() => {
          setActiveColumn('RETURN_WINDOW');
        }}
      >
        {logbookRecord.return_window}
      </div>

      {/* Taxes & Fees */}
      <div
        className={cn(
          CELL_CLASSNAME,
          'text-right',
          COLUMN_WIDTHS.CA_TAX_FEES_REQUIRED,
          {
            [ACTIVE_CELL_CLASSNAME]:
              activeColumn === 'CA_TAX_FEES_REQUIRED' && isActiveRow,
          },
        )}
        onClick={() => {
          setActiveColumn('CA_TAX_FEES_REQUIRED');
        }}
      >
        {logbookRecord.ca_tax_fees_required
          ? 'Yes'
          : logbookRecord.ca_tax_fees_required === false
            ? 'No'
            : ''}
      </div>
      <div
        className={cn(
          CELL_CLASSNAME,
          'text-right',
          COLUMN_WIDTHS.AMOUNT_CHARGED,
          {
            [ACTIVE_CELL_CLASSNAME]:
              activeColumn === 'AMOUNT_CHARGED' && isActiveRow,
          },
        )}
        onClick={() => {
          setActiveColumn('AMOUNT_CHARGED');
        }}
      >
        ${centsToDollars(logbookRecord.amount_charged)}
      </div>

      <div
        className={cn(CELL_CLASSNAME, 'text-right', COLUMN_WIDTHS.TAXES_PAID, {
          [ACTIVE_CELL_CLASSNAME]: activeColumn === 'TAXES_PAID' && isActiveRow,
        })}
        onClick={() => {
          setActiveColumn('TAXES_PAID');
        }}
      >
        <CurrencyInputCell
          fieldName="taxes_paid"
          logbookRecord={logbookRecord}
          onSubmit={onSubmit}
          onSuccess={onSuccess}
          onError={onError}
        />
      </div>
      <div
        className={cn(
          CELL_CLASSNAME,
          'text-right',
          COLUMN_WIDTHS.DMV_FEES_PAID,
          {
            [ACTIVE_CELL_CLASSNAME]:
              activeColumn === 'DMV_FEES_PAID' && isActiveRow,
          },
        )}
        onClick={() => {
          setActiveColumn('DMV_FEES_PAID');
        }}
      >
        <CurrencyInputCell
          fieldName="dmv_fees_paid"
          logbookRecord={logbookRecord}
          onSubmit={onSubmit}
          onSuccess={onSuccess}
          onError={onError}
        />
      </div>
      <div
        className={cn(
          CELL_CLASSNAME,
          'text-right',
          COLUMN_WIDTHS.LESS_PENALTIES,
          {
            [ACTIVE_CELL_CLASSNAME]:
              activeColumn === 'LESS_PENALTIES' && isActiveRow,
          },
        )}
        onClick={() => {
          setActiveColumn('LESS_PENALTIES');
        }}
      >
        <CurrencyInputCell
          fieldName="less_penalties"
          logbookRecord={logbookRecord}
          onSubmit={onSubmit}
          onSuccess={onSuccess}
          onError={onError}
        />
      </div>
      <div
        className={cn(
          CELL_CLASSNAME,
          'text-right',
          COLUMN_WIDTHS.TOTAL_DMV_PAYMENT,
          {
            [ACTIVE_CELL_CLASSNAME]:
              activeColumn === 'TOTAL_DMV_PAYMENT' && isActiveRow,
          },
        )}
        onClick={() => {
          setActiveColumn('TOTAL_DMV_PAYMENT');
        }}
      >
        ${centsToDollars(logbookRecord.total_dmv_payment)}
      </div>
      <div
        className={cn(
          CELL_CLASSNAME,
          'text-right',
          COLUMN_WIDTHS.ACTUAL_AMOUNT,
          {
            [ACTIVE_CELL_CLASSNAME]:
              activeColumn === 'ACTUAL_AMOUNT' && isActiveRow,
          },
        )}
        onClick={() => {
          setActiveColumn('ACTUAL_AMOUNT');
        }}
      >
        ${centsToDollars(logbookRecord.actual_amount)}
      </div>
      <div
        className={cn(CELL_CLASSNAME, 'text-right', COLUMN_WIDTHS.REFUND, {
          [ACTIVE_CELL_CLASSNAME]: activeColumn === 'REFUND' && isActiveRow,
        })}
        onClick={() => {
          setActiveColumn('REFUND');
        }}
      >
        ${centsToDollars(logbookRecord.refund)}
      </div>
      <div
        className={cn(
          CELL_CLASSNAME,
          'text-right',
          COLUMN_WIDTHS.EVER_FEE_REFUND,
          {
            [ACTIVE_CELL_CLASSNAME]:
              activeColumn === 'EVER_FEE_REFUND' && isActiveRow,
          },
        )}
        onClick={() => {
          setActiveColumn('EVER_FEE_REFUND');
        }}
      >
        <CurrencyInputCell
          fieldName="ever_fee_refund"
          logbookRecord={logbookRecord}
          onSubmit={onSubmit}
          onSuccess={onSuccess}
          onError={onError}
        />
      </div>
    </div>
  );
};

const VirtualizedFrozenRows = ({
  logbook,
  scrollPosition,
  activeIndex,
  activeRow,
  setActiveRow,
  setActiveColumn,
  activeColumn,
  numOfFilteredLogbookRecords,
}: {
  logbook: LogbookRecord[];
  scrollPosition: number;
  activeIndex: number;
  activeRow: number;
  setActiveRow: (row: number) => void;
  setActiveColumn: (column: ColumnId) => void;
  activeColumn: ColumnId | null;
  numOfFilteredLogbookRecords: number;
}) => {
  const Row = ({
    index,
    style,
  }: {
    index: number;
    style: React.CSSProperties;
  }) => {
    if (index === logbook.length) {
      return (
        <div style={style}>
          <ClearFindInViewFilter
            message={
              <div className="text-xs text-gray-500 whitespace-nowrap">
                <span className="text-black">
                  {`${numOfFilteredLogbookRecords} records`} hidden by filters
                </span>
              </div>
            }
          />
        </div>
      );
    }

    const logbookRecord = logbook[index];
    return (
      <div
        style={style}
        className={cn('bg-white border-r border-black', {
          'bg-blue-100': index === activeIndex,
        })}
        onClick={() => {
          setActiveRow(index);
        }}
      >
        <FrozenRowFragment
          logbookRecord={logbookRecord}
          setActiveColumn={setActiveColumn}
          isActiveRow={activeRow === index}
          activeColumn={activeColumn}
        />
      </div>
    );
  };

  const ref = useRef<FixedSizeList<Deal>>(null);

  useEffect(() => {
    if (!ref.current) return;
    // DEV NOTE: SCROLL-TWO-VIRTUALIZED-LISTS-TOGETHER-HACK
    // Look above for the description of the hack
    // We need the two lists to scroll together in sync
    // so we need to listen to the document scroll event
    // and update the scrollPosition state

    ref.current.scrollTo?.(scrollPosition);
  }, [scrollPosition, ref.current]);

  return (
    <div className="virtualized-frozen-rows-list h-screen w-[480px] z-2">
      <AutoSizer>
        {({ height, width }) => {
          return (
            <FixedSizeList
              className="List"
              // DEV NOTE: SCROLL-TWO-VIRTUALIZED-LISTS-TOGETHER-HACK
              // Look above for the description of the hack
              // This overflow: hidden is important to prevent the list from scrolling
              // when the user scrolls and this list scroll is disabled
              // it forces the scroll event to be triggered at the document level
              style={{ overflow: 'hidden !important' }}
              height={height}
              // +1 for the clear find in view filter
              itemCount={logbook.length + 1}
              itemSize={20}
              width={width}
              ref={ref}
            >
              {Row}
            </FixedSizeList>
          );
        }}
      </AutoSizer>
    </div>
  );
};

const VirtualizedUnfrozenRows = ({
  logbook,
  scrollPosition,
  activeIndex,
  activeRow,
  activeColumn,
  setActiveRow,

  setActiveColumn,
}: {
  activeRow: number;
  activeColumn: ColumnId | null;
  logbook: LogbookRecord[];
  scrollPosition: number;
  activeIndex: number;
  setActiveRow: (row: number) => void;
  setActiveColumn: (column: ColumnId) => void;
}) => {
  const Row = ({
    index,
    style,
  }: {
    index: number;
    style: React.CSSProperties;
  }) => {
    const logbookRecord = logbook[index];
    return (
      <div
        style={style}
        className={cn('bg-white', {
          'bg-blue-100': index === activeIndex,
        })}
        onClick={() => {
          setActiveRow(index);
        }}
      >
        <UnfrozenRowFragment
          logbookRecord={logbookRecord}
          isActiveRow={activeRow === index}
          activeColumn={activeColumn}
          setActiveColumn={setActiveColumn}
        />
      </div>
    );
  };

  const ref = useRef<FixedSizeList<Deal>>(null);

  useEffect(() => {
    if (!ref.current) return;
    // DEV NOTE: SCROLL-TWO-VIRTUALIZED-LISTS-TOGETHER-HACK
    // Look above for the description of the hack
    // We need the two lists to scroll together in sync
    // so we need to listen to the document scroll event
    // and update the scrollPosition state

    ref.current.scrollTo?.(scrollPosition);
  }, [scrollPosition, ref.current]);

  return (
    <div className="virtualized-unfrozen-rows-list w-full h-screen">
      <AutoSizer>
        {({ height, width }) => {
          return (
            <FixedSizeList
              className="List h"
              // DEV NOTE: SCROLL-TWO-VIRTUALIZED-LISTS-TOGETHER-HACK
              // Look above for the description of the hack
              // This overflow: hidden is important to prevent the list from scrolling
              // when the user scrolls and this list scroll is disabled
              // it forces the scroll event to be triggered at the document level
              style={{ overflow: 'hidden !important' }}
              height={height}
              itemCount={logbook.length}
              itemSize={20}
              width={width}
              ref={ref}
            >
              {Row}
            </FixedSizeList>
          );
        }}
      </AutoSizer>
    </div>
  );
};

const LastSaved = () => {
  const { lastSaved } = useLogBookContext();
  const lastSavedWithin3Seconds = lastSaved
    ? dayjs().diff(lastSaved, 'seconds') < 3
    : false;
  const [, setTime] = useState(dayjs());

  useEffect(() => {
    const intervalId = setInterval(() => {
      setTime(dayjs());
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  if (!lastSavedWithin3Seconds) return null;

  return (
    <div className="flex flex-row text-sm items-center px-2 text-black border-black gap-2">
      <CheckCircle className="size-3 text-green-500" />
      <div>Saved</div>
    </div>
  );
};

const LogBook = () => {
  const { logbook: unfilteredLogbook } = useLogbookContext();
  const { id: cdkIdToFocus } = useGetFocusId({ uiType: 'logbook-record' });
  const { findInViewQuery } = useFindInViewFilterContext();
  const logbook = unfilteredLogbook.filter((record) => {
    if (!findInViewQuery) return true;

    // TECH DEBT: This is a hack to filter the logbook
    // This does account for formatting like currency which has a decimal point
    return JSON.stringify(record)
      .toLowerCase()
      .includes(findInViewQuery.toLowerCase());
  });
  const scrollRowIntoView = useCallback((rowToFocus: number) => {
    if (rowToFocus !== -1) {
      const windowHeight = window.innerHeight;
      // scroll to the middle of the page
      // indexToFocus * 20 is the position of the item in the list
      // windowHeight / 2 is the middle of the page
      // so we subtract the middle of the page from the position of the item in the list
      // to get the new scroll position
      const newScrollPosition = rowToFocus * 20 - windowHeight / 2;

      // The window position has to scroll to the newScrollPosition as well otherwise
      // the view port will not be in the correct position for the virtualized lists
      // Thus we need to scroll the window to the newScrollPosition
      // and then set the scrollPosition state for the virtualized lists
      // to show the correct virtualized view port
      window.scrollTo({
        top: newScrollPosition,
        behavior: 'instant',
      });
      setScrollPosition(newScrollPosition);
    }
  }, []);

  const rowToFocus = useMemo(
    () =>
      logbook.findIndex((record) => record.cdk_deal_id == Number(cdkIdToFocus)),
    [logbook, cdkIdToFocus],
  );

  const [activeRow, setActiveRow] = useState(rowToFocus);
  const [activeColumn, setActiveColumn] = useState<ColumnId | null>(null);

  const [recordToFocus, setRecordToFocus] = useState<LogbookRecord | null>(
    logbook[rowToFocus],
  );

  useEffect(() => {
    // DEV NOTE: The only time the activeRow is from the focus on mount
    // and when a user clicks a cell, so we want to know the actually record
    // that the user has intended to focus on, not just the row
    // The row index is unreliable because the user may have filtered the logbook
    // Thus we set the recordToFocus to the record at the activeRow index
    setRecordToFocus(logbook[activeRow]);
  }, [activeRow]);

  useEffect(() => {
    const newRowToFocus = logbook.findIndex(
      (record) => record.cdk_deal_id == recordToFocus?.cdk_deal_id,
    );

    // UX NOTE: if we find the rowToFocus, we want to scroll to it
    // otherwise we want to scroll to the top of the page
    // and reset the scroll position

    if (newRowToFocus === -1) {
      // UX NOTE: If we don't reset the Y scroll position,
      // after the user has typed in the find in view query,
      // the virtualized list may be in the wrong position and show nothing
      // which is confusing for the user
      // Thus, we want to scroll to the top of the page and reset the scroll position
      // when the find in view query changes
      // But we don't want to change the X scroll position because the user may want
      // to focus on a specific column
      window.scrollTo({
        top: 0,
        behavior: 'instant',
      });
      setScrollPosition(0);
      return;
    }
    setActiveRow(newRowToFocus);
    scrollRowIntoView(newRowToFocus);
  }, [findInViewQuery]);

  const [scrollPosition, setScrollPosition] = useState(0);

  const scrollListener = useCallback(() => {
    setScrollPosition(window.scrollY);
  }, []);

  const handleHorizontalScrollAfterHotkeyPress = (
    nextColumn: ColumnId | null,
  ) => {
    if (!nextColumn) return;
    // Handle scroll so that the next column is in view
    // at the bottom of the page
    // if it's not already in view

    // sum all the widths of all the columns before the current column
    // without the frozen columns
    // Handle scroll so that the next column is in view
    // at the bottom of the page
    // if it's not already in view

    // sum all the widths of all the columns before the current column
    // without the frozen columns
    const nextColumnIndex = COLUMNS.findIndex(
      (column) => column === nextColumn,
    );
    const columnsBeforeCurrentColumn = COLUMNS.slice(0, nextColumnIndex);
    const pxLeftOfColumn = columnsBeforeCurrentColumn.reduce((acc, column) => {
      // frozen columns are not included
      if (FROZEN_COLUMNS.includes(column)) {
        return acc;
      }
      const width = COLUMN_WIDTHS[column];
      const tailwindNumber = width.split('-')[1];
      return acc + parseInt(tailwindNumber) * 4;
    }, 0);

    // DEV NOTE (jimmy): This is very brittle and needs to be refactored
    // but it works for now
    const frozenColumnsWidth = 528;
    const navWidth = 255;
    const viewportWidthOfUnfrozenColumns =
      window.innerWidth - (frozenColumnsWidth + navWidth); // 528 is the width of the frozen columns and nav

    const nextColumnTailwindWidth = COLUMN_WIDTHS[nextColumn].split('-')[1];
    const nextColumnWidth = parseInt(nextColumnTailwindWidth) * 4;

    const nextColumnRightSideIsBeyondViewportToTheRight =
      pxLeftOfColumn + nextColumnWidth >
      viewportWidthOfUnfrozenColumns + window.scrollX;
    if (nextColumnRightSideIsBeyondViewportToTheRight) {
      const newScrollPosition =
        pxLeftOfColumn + nextColumnWidth - viewportWidthOfUnfrozenColumns + 2;
      window.scrollTo({
        left: newScrollPosition,
        behavior: 'instant',
      });
      return;
    }

    const nextColumnLeftSideIsBeyondViewportToTheLeft =
      pxLeftOfColumn < window.scrollX;
    if (nextColumnLeftSideIsBeyondViewportToTheLeft) {
      const newScrollPosition = pxLeftOfColumn;
      window.scrollTo({
        left: newScrollPosition,
        behavior: 'instant',
      });
      return;
    }
  };
  const handleVerticalScrollAfterHotkeyPress = (nextRow: number) => {
    // Handle scroll so that the next row is in view
    // at the bottom of the page
    // if it's not already in view

    // DEV NOTE (jimmy): This is extremely brittle and needs to be refactored
    const nextRowIsBelowTheBottomOfThePage =
      nextRow * 20 > window.scrollY + window.innerHeight + 10 - 20 * 4;
    if (nextRowIsBelowTheBottomOfThePage) {
      const newScrollPosition =
        nextRow * 20 - window.innerHeight - 10 + 20 * 3 + 12;
      window.scrollTo({
        top: newScrollPosition,
        behavior: 'instant',
      });
      setScrollPosition(newScrollPosition);

      return;
    }

    const nextRowIsAboveTheTopOfThePage = nextRow * 20 < window.scrollY + 12;
    if (nextRowIsAboveTheTopOfThePage) {
      const newScrollPosition = nextRow * 20;
      window.scrollTo({
        top: newScrollPosition,
        behavior: 'instant',
      });

      return;
    }
  };

  useHotkeys(['tab', 'ArrowRight'], (e) => {
    e.preventDefault();
    e.stopPropagation();

    // go to the next column
    const currentColumnIndex = COLUMNS.findIndex(
      (column) => column === activeColumn,
    );
    const nextColumn = COLUMNS[currentColumnIndex + 1];
    setActiveColumn(nextColumn);

    handleHorizontalScrollAfterHotkeyPress(nextColumn);
    handleVerticalScrollAfterHotkeyPress(activeRow);
  });

  useHotkeys(['shift+tab', 'ArrowLeft'], (e) => {
    e.preventDefault();
    e.stopPropagation();

    // go to the previous column
    const currentColumnIndex = COLUMNS.findIndex(
      (column) => column === activeColumn,
    );
    const previousColumn = COLUMNS[currentColumnIndex - 1];
    setActiveColumn(previousColumn);

    handleHorizontalScrollAfterHotkeyPress(previousColumn);
    handleVerticalScrollAfterHotkeyPress(activeRow);
  });

  useHotkeys(['enter', 'ArrowDown'], (e) => {
    e.preventDefault();
    e.stopPropagation();

    // go to next row
    const nextRow = activeRow + 1;
    setActiveRow(nextRow);

    // Handle scroll so that the next row is in view
    // at the bottom of the page
    // if it's not already in view
    handleVerticalScrollAfterHotkeyPress(nextRow);
    handleHorizontalScrollAfterHotkeyPress(activeColumn);
  });

  useHotkeys(['shift+enter', 'ArrowUp'], (e) => {
    e.preventDefault();
    e.stopPropagation();

    // go to previous row
    const previousRow = activeRow - 1;
    setActiveRow(previousRow);

    // Handle scroll so that the previous row is in view
    // at the bottom of the page
    // if it's not already in view
    handleVerticalScrollAfterHotkeyPress(previousRow);
    handleHorizontalScrollAfterHotkeyPress(activeColumn);
  });

  // DEV NOTE: SCROLL-TWO-VIRTUALIZED-LISTS-TOGETHER-HACK
  // Look above for the description of the hack
  // This useEffect is enabled, by setting both virtualizedFrozenRows and virtualizedUnfrozenRows
  // to have overflow: hidden, then the scroll event gets triggered at the document level!
  // This useEffect listens to the document scroll event and updates the scrollPosition state
  // which is passed down to the VirtualizedFrozenRows and VirtualizedUnfrozenRows components
  useEffect(() => {
    document.addEventListener('scroll', scrollListener);
    return () => {
      document.removeEventListener('scroll', scrollListener);
    };
  }, [scrollListener]);

  const [hasAlreadyScrolledToFocusedRow, setHasAlreadyScrolledToFocusedRow] =
    useState(false);

  useEffect(() => {
    if (hasAlreadyScrolledToFocusedRow) return;
    // UX NOTE: for a url with a cdkIdToFocus, we want to scroll to the item in the list
    // e.g. `/logbook?focus=logbook-record-799`
    if (rowToFocus !== -1) {
      const windowHeight = window.innerHeight;
      // scroll to the middle of the page
      // indexToFocus * 20 is the position of the item in the list
      // windowHeight / 2 is the middle of the page
      // so we subtract the middle of the page from the position of the item in the list
      // to get the new scroll position
      const newScrollPosition = rowToFocus * 20 - windowHeight / 2;

      // The window position has to scroll to the newScrollPosition as well otherwise
      // the view port will not be in the correct position for the virtualized lists
      // Thus we need to scroll the window to the newScrollPosition
      // and then set the scrollPosition state for the virtualized lists
      // to show the correct virtualized view port
      window.scrollTo({
        top: newScrollPosition,
        behavior: 'instant',
      });
      setScrollPosition(newScrollPosition);
      setHasAlreadyScrolledToFocusedRow(true);
    }
  }, [rowToFocus]);

  const [
    hasAlreadySetFocusedRowToActiveRow,
    setHasAlreadySetFocusedRowToActiveRow,
  ] = useState(false);
  useEffect(() => {
    if (hasAlreadySetFocusedRowToActiveRow) return;
    if (isEmpty(logbook)) return;
    // UX NOTE: on the intial load, the logbook data may not be loaded yet
    // so we need to wait for the logbook data to be loaded
    // which will force the rowToFocus to be set
    // and then set the activeRow to the rowToFocus
    setActiveRow(rowToFocus);
    setActiveColumn('CDK_ID');
    setHasAlreadySetFocusedRowToActiveRow(true);
  }, [rowToFocus, logbook]);

  const FOCUSED_COLUMN_CLASSNAME = ' bg-blue-100';
  const { isSaving } = useLogBookContext();

  return (
    <div
      className="flex flex-col gap-0 min-h-screen"
      style={{
        // UI NOTE: for the header to be sticky the whole list needs to be this height
        // But it cannot be detected by css because we have multiple virtualized lists
        // so we need to set the height manually using math
        //  20 * deals.length - for height of the list
        // + 20 - for the header
        height: 20 * logbook.length + 20,
      }}
    >
      <div className="flex flex-row sticky top-0 bg-white z-2">
        {/* Page Header */}
        {/* sticky left-[256px] - is the width of the nav */}
        <div className="flex flex-col gap-0  border-black border-t sticky left-[256px] z-20 bg-white">
          <div className="flex flex-row justify-between w-full">
            <div className="flex flex-row h-5 items-center gap-2">
              <div className="text-md  items-center px-2 text-black border-black border-b border-t border-l">
                Logbook
              </div>
              {isSaving && (
                <div className="flex flex-row text-sm items-center px-2 text-black border-black gap-2">
                  <RefreshCw className="size-3 animate-spin" />
                  <div>Saving...</div>
                </div>
              )}
              <LastSaved />
            </div>
            <FindInViewFilter />
          </div>
          <div className="flex flex-row gap-0 items-center whitespace-nowrap border-black border-y sticky top-2 bg-white z-1">
            <div
              className={cn(CELL_CLASSNAME, COLUMN_WIDTHS.VIN, {
                [FOCUSED_COLUMN_CLASSNAME]: activeColumn === 'VIN',
              })}
            >
              VIN
            </div>
            <div
              className={cn(CELL_CLASSNAME, COLUMN_WIDTHS.CDK_ID, {
                [FOCUSED_COLUMN_CLASSNAME]: activeColumn === 'CDK_ID',
              })}
            >
              CDK ID
            </div>
            <div
              className={cn(
                CELL_CLASSNAME,
                COLUMN_WIDTHS.FULL_NAME,
                'border-r-2 border-black',
                {
                  [FOCUSED_COLUMN_CLASSNAME]: activeColumn === 'FULL_NAME',
                },
              )}
            >
              Full Name
            </div>
          </div>
        </div>
        {/* Logbook Headers */}
        <div className="flex flex-col gap-0  border-black border-t z-0">
          <div className="flex flex-row h-5 items-center"></div>
          <div className="flex flex-row gap-0 items-center whitespace-nowrap border-black border-y bg-white z-1">
            <div
              className={cn(CELL_CLASSNAME, COLUMN_WIDTHS.CDK_STATUS, {
                [FOCUSED_COLUMN_CLASSNAME]: activeColumn === 'CDK_STATUS',
              })}
            >
              CDK Status
            </div>
            <div
              className={cn(CELL_CLASSNAME, COLUMN_WIDTHS.CONTRACT_DATE, {
                [FOCUSED_COLUMN_CLASSNAME]: activeColumn === 'CONTRACT_DATE',
              })}
            >
              Contract Date
            </div>
            <div
              className={cn(
                CELL_CLASSNAME,
                COLUMN_WIDTHS.PURCHASE_PRICE_SUBTOTAL,
                {
                  [FOCUSED_COLUMN_CLASSNAME]:
                    activeColumn === 'PURCHASE_PRICE_SUBTOTAL',
                },
              )}
            >
              Purchase Price Subtotal
            </div>
            <div
              className={cn(CELL_CLASSNAME, COLUMN_WIDTHS.CASH_PRICE, {
                [FOCUSED_COLUMN_CLASSNAME]: activeColumn === 'CASH_PRICE',
              })}
            >
              Cash Price
            </div>
            <div
              className={cn(CELL_CLASSNAME, COLUMN_WIDTHS.DELIVERY_FEE, {
                [FOCUSED_COLUMN_CLASSNAME]: activeColumn === 'DELIVERY_FEE',
              })}
            >
              Delivery Fee
            </div>
            <div
              className={cn(CELL_CLASSNAME, COLUMN_WIDTHS.NET_TRADE, {
                [FOCUSED_COLUMN_CLASSNAME]: activeColumn === 'NET_TRADE',
              })}
            >
              Net Trade
            </div>
            <div
              className={cn(CELL_CLASSNAME, COLUMN_WIDTHS.REGISTRATION_STATE, {
                [FOCUSED_COLUMN_CLASSNAME]:
                  activeColumn === 'REGISTRATION_STATE',
              })}
            >
              Registration State
            </div>
            {/* // IRS */}
            <div
              className={cn(CELL_CLASSNAME, COLUMN_WIDTHS.IRS_DOWN_PAYMENT, {
                [FOCUSED_COLUMN_CLASSNAME]: activeColumn === 'IRS_DOWN_PAYMENT',
              })}
            >
              IRSDownPayment
            </div>
            <div
              className={cn(CELL_CLASSNAME, COLUMN_WIDTHS.IRS_SALES_PRICE, {
                [FOCUSED_COLUMN_CLASSNAME]: activeColumn === 'IRS_SALES_PRICE',
              })}
            >
              IRSSalesPrice
            </div>
            <div
              className={cn(CELL_CLASSNAME, COLUMN_WIDTHS.LIENHOLDER, {
                [FOCUSED_COLUMN_CLASSNAME]: activeColumn === 'LIENHOLDER',
              })}
            >
              Lienholder
            </div>
            <div
              className={cn(CELL_CLASSNAME, COLUMN_WIDTHS.CASH_DOWN, {
                [FOCUSED_COLUMN_CLASSNAME]: activeColumn === 'CASH_DOWN',
              })}
            >
              Cash Down
            </div>
            <div
              className={cn(CELL_CLASSNAME, COLUMN_WIDTHS.RETURN_WINDOW, {
                [FOCUSED_COLUMN_CLASSNAME]: activeColumn === 'RETURN_WINDOW',
              })}
            >
              Return Window
            </div>

            {/* Taxes & Fees */}
            <div
              className={cn(
                CELL_CLASSNAME,
                COLUMN_WIDTHS.CA_TAX_FEES_REQUIRED,
                {
                  [FOCUSED_COLUMN_CLASSNAME]:
                    activeColumn === 'CA_TAX_FEES_REQUIRED',
                },
              )}
            >
              CA $ Required
            </div>
            <div
              className={cn(CELL_CLASSNAME, COLUMN_WIDTHS.AMOUNT_CHARGED, {
                [FOCUSED_COLUMN_CLASSNAME]: activeColumn === 'AMOUNT_CHARGED',
              })}
            >
              Amount Charged
            </div>
            <div
              className={cn(CELL_CLASSNAME, COLUMN_WIDTHS.TAXES_PAID, {
                [FOCUSED_COLUMN_CLASSNAME]: activeColumn === 'TAXES_PAID',
              })}
            >
              Paid - Taxes
            </div>
            <div
              className={cn(CELL_CLASSNAME, COLUMN_WIDTHS.DMV_FEES_PAID, {
                [FOCUSED_COLUMN_CLASSNAME]: activeColumn === 'DMV_FEES_PAID',
              })}
            >
              Paid - DMV Fees
            </div>
            <div
              className={cn(CELL_CLASSNAME, COLUMN_WIDTHS.LESS_PENALTIES, {
                [FOCUSED_COLUMN_CLASSNAME]: activeColumn === 'LESS_PENALTIES',
              })}
            >
              Less Penalties
            </div>
            <div
              className={cn(CELL_CLASSNAME, COLUMN_WIDTHS.TOTAL_DMV_PAYMENT, {
                [FOCUSED_COLUMN_CLASSNAME]:
                  activeColumn === 'TOTAL_DMV_PAYMENT',
              })}
            >
              Total DMV Payment
            </div>
            <div
              className={cn(CELL_CLASSNAME, COLUMN_WIDTHS.ACTUAL_AMOUNT, {
                [FOCUSED_COLUMN_CLASSNAME]: activeColumn === 'ACTUAL_AMOUNT',
              })}
            >
              Actual Amount
            </div>
            <div
              className={cn(CELL_CLASSNAME, COLUMN_WIDTHS.REFUND, {
                [FOCUSED_COLUMN_CLASSNAME]: activeColumn === 'REFUND',
              })}
            >
              Refund
            </div>
            <div
              className={cn(CELL_CLASSNAME, COLUMN_WIDTHS.EVER_FEE_REFUND, {
                [FOCUSED_COLUMN_CLASSNAME]: activeColumn === 'EVER_FEE_REFUND',
              })}
            >
              Ever Fee Refund
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-row">
        {/* sticky left-[256px] - is the width of the nav */}
        <div className="flex flex-col h-screen gap-0 border-black sticky left-[256px] z-1 bg-white">
          <VirtualizedFrozenRows
            logbook={logbook}
            scrollPosition={scrollPosition}
            activeIndex={activeRow}
            activeRow={activeRow}
            setActiveRow={setActiveRow}
            setActiveColumn={setActiveColumn}
            activeColumn={activeColumn}
            numOfFilteredLogbookRecords={
              unfilteredLogbook.length - logbook.length
            }
          />
        </div>
        <VirtualizedUnfrozenRows
          logbook={logbook}
          scrollPosition={scrollPosition}
          activeIndex={activeRow}
          activeRow={activeRow}
          activeColumn={activeColumn}
          setActiveRow={setActiveRow}
          setActiveColumn={setActiveColumn}
        />
      </div>
    </div>
  );
};

const LogBookContext = createContext<{
  isSaving: boolean;
  lastSaved: Date | null;
  setIsSaving: (isSaving: boolean) => void;
  setLastSaved: (lastSaved: Date | null) => void;
}>({
  isSaving: false,
  lastSaved: null,
  setIsSaving: () => {},
  setLastSaved: () => {},
});

const LogBookProvider = ({ children }: { children: React.ReactNode }) => {
  // TECH DEBT TODO (jimmy): other state should be moved to the LogBookContext
  // like the activeRow, activeColumn, scrollPosition, etc.
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  return (
    <LogBookContext.Provider
      value={{ isSaving, lastSaved, setIsSaving, setLastSaved }}
    >
      {children}
    </LogBookContext.Provider>
  );
};

const useLogBookContext = () => {
  const context = useContext(LogBookContext);
  if (!context) {
    throw new Error('useLogBookContext must be used within a LogBookProvider');
  }

  return context;
};

function LogbookPage() {
  return (
    <div className="flex flex-col">
      <div className="hidden">
        {/* THIS IS HACK TO GET ALL THE TAILWIND CSS TO LOAD */}
        <div className="w-60" />
        <div className="w-56" />
        <div className="w-52" />
        <div className="w-48" />
        <div className="w-44" />
        <div className="w-40" />
        <div className="w-36" />
        <div className="w-32" />
        <div className="w-30" />
        <div className="w-28" />
        <div className="w-24" />
        <div className="w-20" />
      </div>

      <LogBookProvider>
        <LogBook />
      </LogBookProvider>
    </div>
  );
}
